import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, Select, Button, Spin, Typography, Space, Tag } from 'antd';
import { PrinterOutlined, TeamOutlined, TableOutlined } from '@ant-design/icons';
import api from '../api/client';
import './TimesheetPage.css';

const { Title, Text } = Typography;

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

interface Guard {
  id: number;
  last_name: string;
  first_name: string;
  middle_name: string | null;
}

interface DayInfo {
  day: number;
  weekday: number;
  weekdayName: string;
  isSunday: boolean;
}

const getOrderedMonths = (): string[] => {
  const currentMonth = new Date().getMonth();
  const ordered: string[] = [];
  for (let i = 1; i <= 12; i++) {
    ordered.push(MONTHS[(currentMonth + i) % 12]);
  }
  return ordered;
};

const getDaysInMonth = (monthName: string): { days: DayInfo[]; year: number } => {
  const monthIndex = MONTHS.indexOf(monthName);
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  let year = currentYear;
  if (monthIndex < currentMonth) {
    year = currentYear + 1;
  }
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const days: DayInfo[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIndex, day);
    const weekday = date.getDay();
    days.push({
      day,
      weekday,
      weekdayName: WEEKDAYS[weekday],
      isSunday: weekday === 0,
    });
  }
  return { days, year };
};

const fullName = (g: Guard): string => {
  const parts = [g.last_name, g.first_name, g.middle_name].filter(Boolean);
  return parts.join(' ');
};

/* === Точные размеры из Excel === */
const DAY_COL_WIDTH = 9.65; // −10% от 10.72, все дни одинаковые
const COL_WIDTHS_MM: Record<string, number> = {
  A: 36.303,
  B: DAY_COL_WIDTH,   C: DAY_COL_WIDTH,  D: DAY_COL_WIDTH,  E: DAY_COL_WIDTH,  F: DAY_COL_WIDTH,
  G: DAY_COL_WIDTH,  H: DAY_COL_WIDTH,  I: DAY_COL_WIDTH,  J: DAY_COL_WIDTH,  K: DAY_COL_WIDTH,
  L: DAY_COL_WIDTH,  M: DAY_COL_WIDTH,  N: DAY_COL_WIDTH,  O: DAY_COL_WIDTH,  P: DAY_COL_WIDTH,
  Q: DAY_COL_WIDTH,  R: DAY_COL_WIDTH,  S: DAY_COL_WIDTH,  T: DAY_COL_WIDTH,  U: DAY_COL_WIDTH,
  V: DAY_COL_WIDTH,  W: DAY_COL_WIDTH,  X: DAY_COL_WIDTH,  Y: DAY_COL_WIDTH,  Z: DAY_COL_WIDTH,
  AA: DAY_COL_WIDTH, AB: DAY_COL_WIDTH, AC: DAY_COL_WIDTH, AD: DAY_COL_WIDTH, AE: DAY_COL_WIDTH,
  AF: DAY_COL_WIDTH,
};

const ROW_HEIGHTS_MM: Record<number, number> = {
  1: 11.906, 2: 5.080, 3: 10.583,
  4: 5.292,  5: 5.292,
  6: 19.367, 7: 19.367, 8: 19.367,
  9: 10.372, 10: 5.292, 11: 5.080,
  12: 5.080, 13: 5.080, 14: 5.080, 15: 5.080, 16: 5.503,
};

// Нумерация строк в веб-версии отличается от Excel:
// Excel row 6 (пустая) удалена, поэтому:
// web row 6 = Excel row 7 (ФИО 1)
// web row 7 = Excel row 8 (ФИО 2)
// web row 8 = Excel row 9 (ФИО 3)
// web row 9 = Excel row 10 (пустая)
// web row 10 = Excel row 11 (легенда)
// web row 11-16 = Excel row 13-18 (инструкции)

const SUNDAY_BG = '#c8c8c8';
const FONT_FAMILY = "Calibri, 'Segoe UI', Arial, sans-serif";

const COL_KEYS = [
  'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P',
  'Q','R','S','T','U','V','W','X','Y','Z','AA','AB','AC','AD','AE','AF'
];

const thin = '1px solid #000';
const medium = '2px solid #000';
const none = 'none';

/* Вспомогательная: стиль ячейки дня на основе точных данных Excel */
const getDayCellStyle = (
  colLetter: string,
  rowType: 'day' | 'weekday' | 'data' | 'empty' | 'legend',
  isSunday: boolean,
  isLastDataRow: boolean = false
): React.CSSProperties => {
  const style: React.CSSProperties = {
    width: `${COL_WIDTHS_MM[colLetter]}mm`,
    borderTop: thin,
    borderBottom: thin,
    textAlign: 'center',
    verticalAlign: 'middle',
    fontFamily: FONT_FAMILY,
    fontSize: '12pt',
    padding: 0,
    background: (isSunday && (rowType === 'day' || rowType === 'weekday')) ? SUNDAY_BG : '#fff',
  };

  // Специальные границы по Excel:
  if (colLetter === 'B') {
    style.borderLeft = thin;
  }

  if (colLetter === 'I') {
    if (rowType === 'weekday') {
      style.borderLeft = none;
      style.borderRight = thin;
    } else {
      style.borderLeft = none;
      style.borderRight = none;
    }
  } else if (colLetter === 'K') {
    style.borderLeft = none;
    style.borderRight = thin;
  } else if (colLetter === 'Y') {
    if (rowType === 'day' || rowType === 'data' || rowType === 'empty') {
      style.borderRight = none;
    } else {
      style.borderRight = thin;
    }
  } else if (colLetter === 'AF') {
    style.borderRight = thin;
  } else {
    style.borderLeft = thin;
    style.borderRight = thin;
  }

  // Для rowType=weekday убираем top border (чтобы не дублировать day row)
  if (rowType === 'weekday') {
    style.borderTop = none;
  }

  // Для последней строки данных (web row 8 = Excel row 9) Z-AA-AB-AC-AD: top=none
  if (isLastDataRow && ['Z','AA','AB','AC','AD'].includes(colLetter)) {
    style.borderTop = none;
  }

  return style;
};

const getNameCellStyle = (isFirstRow: boolean): React.CSSProperties => ({
  width: `${COL_WIDTHS_MM['A']}mm`,
  borderLeft: thin,
  borderRight: none,
  borderTop: isFirstRow ? none : thin,
  borderBottom: thin,
  textAlign: 'left',
  verticalAlign: 'middle',
  fontFamily: FONT_FAMILY,
  fontSize: '16pt',
  padding: '2px 4px',
  wordWrap: 'break-word',
  overflow: 'hidden',
});

/* === Компонент табели === */
const TimesheetPrintView: React.FC<{
  month: string;
  year: number;
  days: DayInfo[];
  guards: Guard[];
}> = ({ month, year, days, guards }) => {
  const dayCount = days.length;
  const dayCells = Array.from({ length: dayCount }, (_, i) => i + 1);

  return (
    <table
      cellPadding={0}
      cellSpacing={0}
      style={{
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        fontFamily: FONT_FAMILY,
        background: '#fff',
        marginTop: '30mm',
      }}
    >
      <colgroup>
        <col key="A" style={{ width: `${COL_WIDTHS_MM['A']}mm` }} />
        {dayCells.map((_, idx) => (
          <col key={COL_KEYS[idx + 1]} style={{ width: `${COL_WIDTHS_MM[COL_KEYS[idx + 1]]}mm` }} />
        ))}
      </colgroup>

      {/* Row 1 — Месяц/Год */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[1]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          fontFamily: FONT_FAMILY,
          fontSize: '28pt',
          lineHeight: 1.2,
          paddingLeft: `${COL_WIDTHS_MM['A']}mm`,
          border: 'none',
        }}>
          {month} {year}
        </td>
      </tr>

      {/* Row 2 — Заголовок */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[2]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          lineHeight: 1.2,
          paddingLeft: `${COL_WIDTHS_MM['A']}mm`,
          border: 'none',
        }}>
          Табель учета рабочего времени отдела охраны
        </td>
      </tr>

      {/* Row 3 — пустая */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[3]}mm` }}>
        <td colSpan={dayCount + 1} style={{ border: 'none' }} />
      </tr>

      {/* Row 4 — Дни */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[4]}mm` }}>
        <td rowSpan={2} style={{
          width: `${COL_WIDTHS_MM['A']}mm`,
          borderLeft: thin,
          borderTop: thin,
          borderBottom: thin,
          borderRight: none,
        }} />
        {dayCells.map((d, idx) => {
          const dayInfo = days.find((x) => x.day === d);
          const colLetter = COL_KEYS[idx + 1];
          return (
            <td key={`day-${d}`} style={getDayCellStyle(colLetter, 'day', !!dayInfo?.isSunday)}>
              {dayInfo ? dayInfo.day : ''}
            </td>
          );
        })}
      </tr>

      {/* Row 5 — Дни недели */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[5]}mm` }}>
        {dayCells.map((d, idx) => {
          const dayInfo = days.find((x) => x.day === d);
          const colLetter = COL_KEYS[idx + 1];
          return (
            <td key={`wd-${d}`} style={getDayCellStyle(colLetter, 'weekday', !!dayInfo?.isSunday)}>
              {dayInfo ? dayInfo.weekdayName : ''}
            </td>
          );
        })}
      </tr>

      {/* Row 6-8 — ФИО охранников (бывший Excel row 7-9) */}
      {guards.map((guard, gIdx) => (
        <tr key={guard.id} style={{ height: `${ROW_HEIGHTS_MM[6]}mm` }}>
          <td style={getNameCellStyle(gIdx === 0)}>
            {fullName(guard)}
          </td>
          {dayCells.map((_, idx) => {
            const colLetter = COL_KEYS[idx + 1];
            const isSunday = days[idx]?.isSunday;
            const isLastRow = gIdx === guards.length - 1;
            return (
              <td key={`${guard.id}-${idx}`} style={getDayCellStyle(colLetter, 'data', !!isSunday, isLastRow)} />
            );
          })}
        </tr>
      ))}

      {/* Row 9 — пустая без границ (бывший Excel row 10) */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[9]}mm` }}>
        <td colSpan={dayCount + 1} style={{ border: 'none' }} />
      </tr>

      {/* Row 10 — Легенда (бывший Excel row 11) */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[10]}mm` }}>
        <td style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          Р-работал
        </td>
        <td colSpan={11} style={{
          border: medium,
          textAlign: 'center',
          verticalAlign: 'middle',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
        }}>
          Праздничный день, двойная оплата
        </td>
        {dayCells.slice(11).map((_, idx) => {
          const colLetter = COL_KEYS[idx + 12];
          return (
            <td key={`r10-${idx}`} style={{
              width: `${COL_WIDTHS_MM[colLetter]}mm`,
              border: 'none',
            }} />
          );
        })}
      </tr>

      {/* Row 11 — пустая */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[11]}mm` }}>
        <td colSpan={dayCount + 1} style={{ border: 'none' }} />
      </tr>

      {/* Row 12-16 — Инструкции (бывший Excel row 13-18) */}
      <tr style={{ height: `${ROW_HEIGHTS_MM[12]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          После окончания дежурства: - не оставляйте открытыми двери и окна в дежурной комнате
        </td>
      </tr>
      <tr style={{ height: `${ROW_HEIGHTS_MM[13]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          - установите обогреватель на поддержание невысокой температуры (около 20 градусов)
        </td>
      </tr>
      <tr style={{ height: `${ROW_HEIGHTS_MM[14]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          Держите дежурную комнату в чистоте. Тряпки (ветошь) можете взять в инструментальной комнате. Не протирайте включенные в сеть электрические приборы.
        </td>
      </tr>
      <tr style={{ height: `${ROW_HEIGHTS_MM[15]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          Свою одежду можете постирать в стиральной машине. Как пользоваться - спросите у сотрудников.
        </td>
      </tr>
      <tr style={{ height: `${ROW_HEIGHTS_MM[16]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '12pt',
          verticalAlign: 'middle',
        }}>
          Перчатки для работы можете взять в магазине у Пензина Андрея Георгиевича.
        </td>
      </tr>
      <tr style={{ height: `${ROW_HEIGHTS_MM[16]}mm` }}>
        <td colSpan={dayCount + 1} style={{
          border: 'none',
          fontFamily: FONT_FAMILY,
          fontSize: '14pt',
          fontWeight: 'bold',
          verticalAlign: 'middle',
        }}>
          Воскресенье: смена с 17:00 до 09:00. Контролируйте себя сами!
        </td>
      </tr>
    </table>
  );
};

/* === Печать === */
const PRINT_SCALE = 0.86; // 287mm / 335mm total width (A4 landscape − 5mm margins)
const FONT_SCALE = 0.75;   // шрифты уменьшаем меньше, чем layout

const openPrintWindow = (printArea: HTMLElement) => {
  const clone = printArea.cloneNode(true) as HTMLElement;
  const w = window.open('', '_blank', 'width=1100,height=800');
  if (!w) return;

  // Рекурсивно уменьшаем все inline-размеры в mm/pt/px
  const scaleElement = (el: HTMLElement) => {
    const s = el.style;

    if (s.width && s.width.includes('mm')) {
      s.width = `${(parseFloat(s.width) * PRINT_SCALE).toFixed(2)}mm`;
    }
    if (s.height && s.height.includes('mm')) {
      s.height = `${(parseFloat(s.height) * PRINT_SCALE).toFixed(2)}mm`;
    }
    if (s.paddingLeft && s.paddingLeft.includes('mm')) {
      s.paddingLeft = `${(parseFloat(s.paddingLeft) * PRINT_SCALE).toFixed(2)}mm`;
    }
    if (s.fontSize && s.fontSize.includes('pt')) {
      s.fontSize = `${(parseFloat(s.fontSize) * FONT_SCALE).toFixed(1)}pt`;
    }
    if (s.padding && s.padding.includes('px')) {
      s.padding = s.padding.replace(/(\d+(?:\.\d+)?)px/g, (_, n) =>
        `${Math.max(1, Math.round(parseFloat(n) * PRINT_SCALE))}px`
      );
    }
    ['border', 'borderTop', 'borderBottom', 'borderLeft', 'borderRight'].forEach((prop) => {
      const v = (s as any)[prop];
      if (v && typeof v === 'string' && v.includes('px')) {
        (s as any)[prop] = v.replace(/(\d+(?:\.\d+)?)px/g, (_, n) =>
          `${Math.max(1, Math.round(parseFloat(n) * PRINT_SCALE))}px`
        );
      }
    });
  };

  scaleElement(clone);
  clone.querySelectorAll('*').forEach((el) => scaleElement(el as HTMLElement));

  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Табель охраны</title>
<style>
  @page { size: A4 landscape; margin: 5mm; }
  html, body { margin: 0; padding: 0; background: #fff; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
</style></head><body>
${clone.outerHTML}
</body></html>`);
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
    setTimeout(() => w.close(), 300);
  }, 100);
};

/* === Основной компонент === */
const TimesheetPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('guards');
  const [guards, setGuards] = useState<Guard[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const printAreaRef = useRef<HTMLDivElement>(null);

  const orderedMonths = useMemo(() => getOrderedMonths(), []);

  useEffect(() => {
    if (!selectedMonth && orderedMonths.length > 0) {
      setSelectedMonth(orderedMonths[0]);
    }
  }, [orderedMonths, selectedMonth]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const posRes = await api.get('/positions');
        const guardPos = posRes.data.find((p: any) =>
          p.name.toLowerCase().includes('торож')
        );
        if (guardPos) {
          const empRes = await api.get('/employees', {
            params: {
              position_id: guardPos.id,
              status: 'active',
              per_page: 100,
            },
          });
          setGuards(empRes.data.items || []);
        }
      } catch (err) {
        console.error('Failed to load guards', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { days, year } = useMemo(() => {
    if (!selectedMonth) return { days: [] as DayInfo[], year: new Date().getFullYear() };
    return getDaysInMonth(selectedMonth);
  }, [selectedMonth]);

  const handlePrint = () => {
    if (printAreaRef.current) {
      openPrintWindow(printAreaRef.current);
    }
  };

  return (
    <div className="timesheet-page">
      <Title level={2}>Табель учёта рабочего времени</Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'guards',
            label: (
              <span>
                <TeamOutlined /> Охранники
              </span>
            ),
            children: (
              <div className="timesheet-guards-tab">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Выберите месяц:</Text>
                    <Select
                      value={selectedMonth}
                      onChange={setSelectedMonth}
                      style={{ width: 200, marginLeft: 12 }}
                      options={orderedMonths.map((m) => ({ value: m, label: m }))}
                    />
                  </div>

                  <div>
                    <Text strong>Список охранников ({guards.length}):</Text>
                    {loading ? (
                      <Spin style={{ marginLeft: 12 }} />
                    ) : (
                      <div className="timesheet-guards-list">
                        {guards.map((g) => (
                          <Tag key={g.id} color="blue" className="timesheet-guard-tag">
                            {fullName(g)}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="primary"
                    icon={<TableOutlined />}
                    onClick={() => setActiveTab('timesheet')}
                  >
                    Сформировать табель
                  </Button>
                </Space>
              </div>
            ),
          },
          {
            key: 'timesheet',
            label: (
              <span>
                <TableOutlined /> Табель
              </span>
            ),
            children: (
              <div className="timesheet-preview-tab">
                <div className="timesheet-print-actions">
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={handlePrint}
                  >
                    Печать
                  </Button>
                </div>

                <div className="timesheet-preview-scroll">
                  <div ref={printAreaRef}>
                    <TimesheetPrintView
                      month={selectedMonth}
                      year={year}
                      days={days}
                      guards={guards}
                    />
                  </div>
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TimesheetPage;
